import { useState } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { FileText, Video, Loader2, ExternalLink, XCircle, CheckCircle2, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import { DietPayButton } from './DietPayButton';

export const fmtWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
};

/** V66 — a document the learner shared into the chat; opens via the scoped consult download. */
export const AttachmentBubble = ({ consultId, message, mine }) => {
  const [busy, setBusy] = useState(null);
  const open = async (a) => {
    setBusy(a.id);
    try {
      const res = await api.get(`/api/medical/consult/${consultId}/vault/${a.id}/download?stream=1`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: a.content_type || res.headers['content-type'] }));
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      const d = e.response?.data?.detailObject || e.response?.data?.detail;
      toast.error((typeof d === 'object' ? d?.message : d) || 'Could not open the document');
    } finally { setBusy(null); }
  };
  return (
    <div data-testid="consult-attachment-bubble">
      <p className={`text-[11px] mb-1 ${mine ? 'text-emerald-100' : 'text-muted-foreground'}`}>{message.content}</p>
      <div className="space-y-1">
        {(message.attachments || []).map(a => (
          <button key={a.id} type="button" onClick={() => open(a)} data-testid={`consult-attachment-${a.id}`}
            className={`w-full flex items-center gap-2 rounded-lg px-2 py-1.5 text-xs text-left ${mine ? 'bg-white/15 hover:bg-white/25' : 'bg-sky-50 hover:bg-sky-100 text-sky-900'}`}>
            {busy === a.id ? <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" /> : <FileText className="h-3.5 w-3.5 shrink-0" />}
            <span className="truncate flex-1">{a.file_name}</span>
            <ExternalLink className="h-3 w-3 shrink-0 opacity-70" />
          </button>
        ))}
      </div>
    </div>
  );
};

/** V72 — the diet plan the dietician shared. Hers to keep: it also sits in her Vault,
 * so this card keeps working long after the chat closes. */
export const DietPlanBubble = ({ message, mine }) => {
  const [busy, setBusy] = useState(false);
  const a = message.attachment || {};
  const open = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/api/vault/download/${a.vault_file_id}?stream=1`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: a.content_type || res.headers['content-type'] }));
      // keep her own filename — opening the blob directly saves it as a bare uuid
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener';
      link.setAttribute('download', a.filename || 'diet-plan.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      const d = e.response?.data?.detailObject || e.response?.data?.detail;
      toast.error((typeof d === 'object' ? d?.message : d) || 'Could not open the diet plan');
    } finally { setBusy(false); }
  };
  return (
    <div data-testid="consult-diet-plan-bubble">
      {message.content ? (
        <p className={`text-[11px] mb-1.5 ${mine ? 'text-emerald-100' : 'text-muted-foreground'}`}>{message.content}</p>
      ) : null}
      <button type="button" onClick={open} data-testid="consult-diet-plan-open"
        className={`w-full flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-left transition-colors ${mine ? 'bg-white/15 hover:bg-white/25' : 'bg-emerald-50 hover:bg-emerald-100 text-emerald-950 border border-emerald-200'}`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin shrink-0" /> : <FileText className="h-4 w-4 shrink-0" />}
        <span className="flex-1 min-w-0">
          <span className="block text-xs font-semibold truncate">{a.title || 'Diet Plan'}</span>
          <span className={`block text-[10px] ${mine ? 'text-emerald-100/80' : 'text-emerald-700'}`}>
            {a.filename}{mine === false && a.kind === 'diet_assessment' ? ' · her answers, ready to work from' : ' · saved to your Vault'}
          </span>
        </span>
        <ExternalLink className="h-3.5 w-3.5 shrink-0 opacity-70" />
      </button>
    </div>
  );
};

/** V73 — the assessment form card, as the mother and the dietician each see it. */
export const AssessmentFormBubble = ({ consultId, message, mine, role, onFill, onReopen }) => {
  const status = message.status || 'sent';
  const open = status === 'sent' || status === 'in_progress';
  return (
    <div data-testid="consult-assessment-form-bubble">
      {message.content ? (
        <p className={`text-[11px] mb-1.5 ${mine ? 'text-emerald-100' : 'text-muted-foreground'}`}>{message.content}</p>
      ) : null}
      <div className={`rounded-xl px-3 py-2.5 ${mine ? 'bg-white/15' : 'bg-emerald-50 border border-emerald-200 text-emerald-950'}`}>
        <div className="flex items-center gap-2">
          <ClipboardList className="h-4 w-4 shrink-0" />
          <span className="text-xs font-semibold flex-1">
            Diet assessment · {message.form_type === 'international' ? 'International' : 'India'}
          </span>
          <span className={`text-[10px] rounded-full px-2 py-0.5 ${status === 'submitted'
            ? 'bg-emerald-600 text-white' : status === 'cancelled' ? 'bg-muted text-muted-foreground' : 'bg-amber-200 text-amber-900'}`}>
            {status === 'submitted' ? 'Filled' : status === 'cancelled' ? 'Cancelled' : status === 'in_progress' ? 'In progress' : 'To fill'}
          </span>
        </div>
        <p className={`text-[11px] mt-1 ${mine ? 'text-emerald-100/80' : 'text-emerald-700'}`}>
          {message.question_count ? `${message.question_count} questions · about 5 minutes` : 'Short assessment'}
        </p>
        {role === 'learner' && open && (
          <Button size="sm" className="mt-2 h-8" onClick={onFill} data-testid="consult-fill-form-btn">
            {status === 'in_progress' ? 'Continue filling' : 'Fill the form'}
          </Button>
        )}
        {role === 'provider' && status === 'submitted' && (
          <Button size="sm" variant="outline" className="mt-2 h-8" onClick={onReopen}
            data-testid="consult-reopen-form-btn">
            Reopen for edits
          </Button>
        )}
      </div>
    </div>
  );
};

/** V66 — dietician's payment request card (learner pays; provider sees status + cancel). */
export const PaymentRequestBubble = ({ message, viewerRole, closed, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const status = message.status || 'pending';
  const cancel = async () => {
    if (!window.confirm('Cancel this payment request?')) return;
    setBusy(true);
    try { await api.post(`/api/medical/payment-requests/${message.payment_request_id}/cancel`); toast.success('Payment request cancelled'); onChanged?.(); }
    catch { toast.error('Could not cancel'); } finally { setBusy(false); }
  };
  const tone = status === 'paid' ? 'border-emerald-300 bg-emerald-50' : status === 'pending' ? 'border-amber-300 bg-amber-50' : 'border-border bg-muted/40';
  return (
    <div className={`rounded-xl border p-3 text-foreground min-w-[220px] ${tone}`} data-testid="consult-payment-request-card">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Payment request</p>
      <p className="text-lg font-semibold" data-testid="consult-payment-amount">{message.amount_str}</p>
      <p className="text-xs">{message.label}</p>
      {message.note && <p className="text-[11px] text-muted-foreground mt-0.5">{message.note}</p>}
      <div className="mt-2 flex items-center gap-2">
        {status === 'paid' && <Badge className="bg-emerald-600 text-white text-[10px]" data-testid="consult-payment-status-paid"><CheckCircle2 className="h-3 w-3 mr-1" />Paid</Badge>}
        {status === 'cancelled' && <Badge variant="outline" className="text-[10px]" data-testid="consult-payment-status-cancelled"><XCircle className="h-3 w-3 mr-1" />Cancelled</Badge>}
        {status === 'expired' && <Badge variant="outline" className="text-[10px]">Expired</Badge>}
        {status === 'pending' && viewerRole === 'learner' && !closed && (
          <DietPayButton requestId={message.payment_request_id} amountStr={message.amount_str} label={message.label} onPaid={() => onChanged?.()} testId="consult-pay-btn" />
        )}
        {status === 'pending' && viewerRole === 'learner' && closed && <Badge variant="outline" className="text-[10px]">Awaiting payment</Badge>}
        {status === 'pending' && viewerRole === 'provider' && (
          <>
            <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-800">Awaiting payment</Badge>
            <Button size="sm" variant="ghost" className="h-7 text-xs text-rose-600" onClick={cancel} disabled={busy} data-testid="consult-payment-cancel-btn">Cancel</Button>
          </>
        )}
        {status === 'pending' && viewerRole === 'superadmin' && <Badge variant="outline" className="text-[10px]">Awaiting payment</Badge>}
      </div>
    </div>
  );
};

/** V66 — scheduled Zoom consultation card (learner: Join · provider: Start as host + Cancel). */
export const VideoCallBubble = ({ message, viewerRole, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const status = message.status || 'scheduled';
  const starts = message.scheduled_at ? new Date(message.scheduled_at) : null;
  const minsUntil = starts ? (starts - new Date()) / 60000 : null;
  const live = minsUntil !== null && minsUntil <= 10 && minsUntil > -(message.duration_minutes || 30);
  const cancel = async () => {
    if (!window.confirm('Cancel this video consultation? The learner will be notified.')) return;
    setBusy(true);
    try { await api.post(`/api/medical/consult-calls/${message.call_id}/cancel`); toast.success('Call cancelled'); onChanged?.(); }
    catch { toast.error('Could not cancel the call'); } finally { setBusy(false); }
  };
  const startHost = async () => {
    try {
      const r = await api.get(`/api/medical/consult-calls/${message.call_id}`);
      window.open(r.data.start_url || r.data.join_url, '_blank', 'noopener');
    } catch { window.open(message.join_url, '_blank', 'noopener'); }
  };
  const tone = status === 'scheduled' ? 'border-sky-300 bg-sky-50' : 'border-border bg-muted/40';
  return (
    <div className={`rounded-xl border p-3 text-foreground min-w-[230px] ${tone}`} data-testid="consult-video-call-card">
      <div className="flex items-center gap-2">
        <div className="h-8 w-8 rounded-full bg-sky-100 flex items-center justify-center shrink-0"><Video className="h-4 w-4 text-sky-700" /></div>
        <div className="min-w-0">
          <p className="text-xs font-semibold">Video consultation</p>
          {(message.scheduled_at || message.duration_minutes) && (
            <p className="text-[11px] text-muted-foreground" data-testid="consult-video-call-when">
              {[message.scheduled_at ? fmtWhen(message.scheduled_at) : null,
                message.duration_minutes ? `${message.duration_minutes} min` : null]
                .filter(Boolean).join(' · ')}
            </p>
          )}
          {message.recording_note && (
            <p className="text-[10px] text-muted-foreground/80 mt-0.5" data-testid="consult-video-call-recording-note">
              {message.recording_note}
            </p>
          )}
        </div>
      </div>
      {message.note && <p className="text-[11px] text-muted-foreground mt-1.5">{message.note}</p>}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {status === 'scheduled' && viewerRole === 'learner' && (
          <a href={message.join_url} target="_blank" rel="noopener noreferrer" data-testid="consult-video-call-join-btn">
            <Button size="sm" className={`h-7 text-xs ${live ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-sky-600 hover:bg-sky-700'} text-white`}>
              <Video className="h-3.5 w-3.5 mr-1" />{live ? 'Join now' : 'Join on Zoom'}
            </Button>
          </a>
        )}
        {status === 'scheduled' && viewerRole === 'provider' && (
          <>
            <Button size="sm" className="h-7 text-xs bg-sky-600 hover:bg-sky-700 text-white" onClick={startHost} data-testid="consult-video-call-start-btn"><Video className="h-3.5 w-3.5 mr-1" />Start (host)</Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs text-rose-600" onClick={cancel} disabled={busy} data-testid="consult-video-call-cancel-btn">{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Cancel'}</Button>
          </>
        )}
        {status === 'cancelled' && <Badge variant="outline" className="text-[10px]"><XCircle className="h-3 w-3 mr-1" />Cancelled</Badge>}
        {status === 'completed' && <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-700"><CheckCircle2 className="h-3 w-3 mr-1" />Done</Badge>}
      </div>
    </div>
  );
};
